import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Plus, Mail, Send, Pause, Play, Copy, Trash2, 
  Eye, Edit, Clock, CheckCircle, XCircle, AlertCircle,
  RefreshCw, BarChart3, Timer, Zap, StopCircle, Download, Users, Upload, X
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';
import QuickImportRecipientsModal from '../components/QuickImportRecipientsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-blue-100 text-blue-800',
  sending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-green-100 text-green-800',
  paused: 'bg-orange-100 text-orange-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-200 text-gray-600'
};

const statusIcons = {
  draft: Edit,
  scheduled: Clock,
  sending: Send,
  sent: CheckCircle,
  paused: Pause,
  failed: XCircle,
  cancelled: StopCircle
};

const EmailCampaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [queueStatuses, setQueueStatuses] = useState({});
  const [quickImportCampaign, setQuickImportCampaign] = useState(null);
  // Failure diagnostics: { campaignId: { loading, summary: [{reason, count, hint, sample_recipients}], total } }
  const [failureDetails, setFailureDetails] = useState({});
  const [failureOpenFor, setFailureOpenFor] = useState(null);

  const token = localStorage.getItem('admin_token');

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const url = filter === 'all' 
        ? `${API_URL}/api/email/campaigns`
        : `${API_URL}/api/email/campaigns?status=${filter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          toast.error('Session expired. Please log in again.');
          navigate('/admin/login');
          return;
        }
        throw new Error(`Failed to fetch campaigns: ${response.status}`);
      }
      
      const data = await response.json();
      setCampaigns(data);
      
      // Fetch queue status for sending/paused campaigns
      const activeCampaigns = data.filter(c => ['sending', 'paused', 'scheduled'].includes(c.status));
      for (const campaign of activeCampaigns) {
        fetchQueueStatus(campaign.id);
      }
    } catch (error) {
      console.error('Fetch campaigns error:', error);
      toast.error(`Failed to load campaigns: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [filter, token, navigate]);

  const fetchQueueStatus = async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/queue-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQueueStatuses(prev => ({ ...prev, [campaignId]: data }));
      }
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchCampaigns();
  }, [token, navigate, fetchCampaigns]);

  // WebSocket connections for active campaigns
  useEffect(() => {
    const activeCampaigns = campaigns.filter(c => ['sending', 'scheduled'].includes(c.status));
    const websockets = [];
    
    // Build WebSocket URL from API URL
    const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
    const wsBaseUrl = API_URL.replace(/^https?/, wsProtocol);
    
    for (const campaign of activeCampaigns) {
      try {
        const ws = new WebSocket(`${wsBaseUrl}/api/email/campaigns/${campaign.id}/ws?token=${token}`);
        
        ws.onopen = () => {
          console.log(`WebSocket connected for campaign ${campaign.id}`);
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'progress' || data.type === 'batch_complete') {
              // Update campaign in state
              setCampaigns(prev => prev.map(c => {
                if (c.id === data.campaign_id) {
                  return {
                    ...c,
                    sent_count: data.sent_count ?? c.sent_count,
                    failed_count: data.failed_count ?? c.failed_count,
                    current_batch: data.current_batch ?? c.current_batch,
                    total_batches: data.total_batches ?? c.total_batches,
                    status: data.status ?? c.status
                  };
                }
                return c;
              }));
              
              // Update queue status
              if (data.recipients) {
                setQueueStatuses(prev => ({
                  ...prev,
                  [data.campaign_id]: {
                    ...prev[data.campaign_id],
                    ...data.recipients,
                    next_batch_at: data.next_batch_at,
                    last_batch_failure_rate: data.last_batch_failure_rate
                  }
                }));
              }
              
              // Show toast for batch completion
              if (data.type === 'batch_complete') {
                toast.success(`Batch ${data.batch_number} completed: ${data.success_count} sent, ${data.failure_count} failed`);
              }
            } else if (data.type === 'status_change') {
              // Update campaign status
              setCampaigns(prev => prev.map(c => {
                if (c.id === data.campaign_id) {
                  return { ...c, status: data.status };
                }
                return c;
              }));
              
              if (data.status === 'sent') {
                toast.success(`Campaign completed successfully!`);
              } else if (data.status === 'failed') {
                toast.error(`Campaign failed: ${data.message || 'Unknown error'}`);
              } else if (data.status === 'paused') {
                toast.warning(`Campaign paused: ${data.message || 'High failure rate detected'}`);
              }
            }
          } catch (err) {
            console.error('Error parsing WebSocket message:', err);
          }
        };
        
        ws.onerror = (error) => {
          console.error(`WebSocket error for campaign ${campaign.id}:`, error);
        };
        
        ws.onclose = () => {
          console.log(`WebSocket closed for campaign ${campaign.id}`);
        };
        
        websockets.push({ campaignId: campaign.id, ws });
      } catch (err) {
        console.error(`Failed to connect WebSocket for campaign ${campaign.id}:`, err);
      }
    }
    
    // Fallback polling for campaigns without WebSocket connection
    const hasActiveCampaigns = activeCampaigns.length > 0;
    let pollInterval;
    if (hasActiveCampaigns) {
      pollInterval = setInterval(() => {
        fetchCampaigns();
      }, 60000); // Fallback poll every 60 seconds
    }
    
    return () => {
      // Close all WebSocket connections
      for (const { ws } of websockets) {
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      }
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [campaigns.length, campaigns.map(c => c.status).join(',')]); // Re-run when campaign count or statuses change

  const createCampaign = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: 'New Campaign',
          subject: 'Email Subject',
          html_body: '<p>Your email content here...</p>'
        })
      });
      
      if (!response.ok) throw new Error('Failed to create campaign');
      
      const campaign = await response.json();
      toast.success('Campaign created');
      navigate(`/admin/email/edit/${campaign.id}`);
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  const duplicateCampaign = async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to duplicate');
      
      toast.success('Campaign duplicated');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to duplicate campaign');
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const pauseCampaign = async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to pause');
      
      toast.success('Campaign paused');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to pause campaign');
    }
  };

  const resumeCampaign = async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to resume');
      
      toast.success('Campaign resumed');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to resume campaign');
    }
  };

  const cancelCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to cancel this campaign? This will stop all remaining sends.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to cancel');
      
      toast.success('Campaign cancelled');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to cancel campaign');
    }
  };

  // Map raw SendGrid / app error strings to a human-readable reason + action hint.
  // Keeps the list short so the UI shows "top reasons" rather than every stack trace.
  const classifyFailure = (errText = '') => {
    const t = (errText || '').toString();
    const lower = t.toLowerCase();
    if (/403\s*forbidden|from\s*address.*not\s*verified|sender identity|does not match a verified Sender/i.test(t)) {
      return {
        reason: '403 — From-address not verified in SendGrid',
        hint: "The campaign's From email isn't an approved sender. In SendGrid → Settings → Sender Authentication → Single Sender Verification, verify the address (or authenticate the domain). Alternately, switch the campaign's From to an already-verified address.",
      };
    }
    if (/401|unauthorized|invalid api key|api key/i.test(t)) {
      return {
        reason: '401 — SendGrid API key invalid',
        hint: 'SENDGRID_API_KEY is missing, revoked, or has no permissions. Regenerate the key in SendGrid → Settings → API Keys and update the production secret.',
      };
    }
    if (/429|rate.?limit|too many requests|over.?limit|credits/i.test(lower)) {
      return {
        reason: '429 — SendGrid rate or quota limit',
        hint: 'The SendGrid account hit its daily / free-tier cap. Wait for the quota reset or upgrade the plan.',
      };
    }
    if (/does not contain a valid address|invalid.*email|400.*bad request|recipient/i.test(lower)) {
      return {
        reason: 'Invalid recipient email',
        hint: 'The recipient address was rejected by SendGrid. Clean the recipient list and re-send.',
      };
    }
    if (/timeout|timed out|ssl|network|connection reset|dns/i.test(lower)) {
      return {
        reason: 'Network error reaching SendGrid',
        hint: 'Transient connectivity issue — retry; if persistent, check outbound egress from the backend host.',
      };
    }
    if (!t.trim()) {
      return { reason: 'Unknown error (no detail)', hint: 'Check the server logs around the send time for the full SendGrid response.' };
    }
    // Fallback: truncate long error strings
    return { reason: t.length > 80 ? t.slice(0, 80) + '…' : t, hint: null };
  };

  const fetchFailureDetails = async (campaignId) => {
    if (failureDetails[campaignId]?.summary) return; // already loaded
    setFailureDetails(prev => ({ ...prev, [campaignId]: { loading: true } }));
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/sends?status=failed`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`${response.status}`);
      const sends = await response.json();
      // Group by classified reason
      const grouped = new Map();
      for (const s of sends) {
        const { reason, hint } = classifyFailure(s.error);
        const bucket = grouped.get(reason) || { reason, hint, count: 0, sample_recipients: [] };
        bucket.count += 1;
        if (bucket.sample_recipients.length < 3 && s.to_email) {
          bucket.sample_recipients.push(s.to_email);
        }
        grouped.set(reason, bucket);
      }
      const summary = Array.from(grouped.values()).sort((a, b) => b.count - a.count);
      setFailureDetails(prev => ({ ...prev, [campaignId]: { summary, total: sends.length, loading: false } }));
    } catch (e) {
      setFailureDetails(prev => ({
        ...prev,
        [campaignId]: { loading: false, error: 'Could not load failure details.' },
      }));
    }
  };

  const toggleFailuresPanel = (campaignId) => {
    const next = failureOpenFor === campaignId ? null : campaignId;
    setFailureOpenFor(next);
    if (next) fetchFailureDetails(next);
  };

  const exportFailedRecipients = async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/failed-recipients/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to export');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `failed_recipients_${campaignId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Failed recipients exported');
    } catch (error) {
      toast.error('Failed to export failed recipients');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#00457f] rounded-lg">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
              <p className="text-gray-500">Create and manage email newsletters</p>
            </div>
          </div>
          <Button
            onClick={createCampaign}
            className="bg-[#00457f] hover:bg-[#003461]"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {['all', 'draft', 'scheduled', 'sending', 'sent', 'paused', 'failed', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => { setFilter(status); }}
                className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors ${
                  filter === status
                    ? 'bg-[#00457f] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchCampaigns}
            className="ml-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Campaigns List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#00457f] border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-500">Loading campaigns...</p>
          </div>
        ) : campaigns.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No campaigns yet</h3>
              <p className="text-gray-500 mb-4">Create your first email campaign to get started.</p>
              <Button onClick={createCampaign}>
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {campaigns.map((campaign) => {
              const StatusIcon = statusIcons[campaign.status] || AlertCircle;
              const queueStatus = queueStatuses[campaign.id];
              const progress = campaign.total_recipients > 0 
                ? Math.round(((campaign.sent_count || 0) / campaign.total_recipients) * 100)
                : 0;
              const isActive = ['sending', 'paused', 'scheduled'].includes(campaign.status);
              
              return (
                <Card key={campaign.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    {/* Row 1: Name + Badges | Actions */}
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${statusColors[campaign.status]}`}>
                          <StatusIcon className="w-3 h-3" />
                          {campaign.status}
                        </span>
                        {campaign.delivery_mode && campaign.delivery_mode !== 'immediate' && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800 flex items-center gap-1">
                            <Timer className="w-3 h-3" />
                            {campaign.delivery_mode === 'throttled' ? 'Throttled' : 'Scheduled'}
                          </span>
                        )}
                      </div>
                      
                      {/* Action Buttons - Compact */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {campaign.status === 'draft' && (
                          <>
                            <Button size="sm" onClick={() => navigate(`/admin/email/edit/${campaign.id}`)}>
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setQuickImportCampaign(campaign)} title="Add recipients" data-testid={`quick-recipients-btn-${campaign.id}`}>
                              <Users className="w-4 h-4 mr-1" />
                              Recipients
                            </Button>
                          </>
                        )}
                        
                        {campaign.status === 'sending' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => pauseCampaign(campaign.id)}>
                              <Pause className="w-4 h-4 mr-1" />
                              Pause
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => cancelCampaign(campaign.id)}>
                              <StopCircle className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </>
                        )}
                        
                        {campaign.status === 'paused' && (
                          <>
                            <Button size="sm" onClick={() => resumeCampaign(campaign.id)}>
                              <Play className="w-4 h-4 mr-1" />
                              Resume
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => cancelCampaign(campaign.id)}>
                              <StopCircle className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </>
                        )}
                        
                        {campaign.status === 'scheduled' && (
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => cancelCampaign(campaign.id)}>
                            <StopCircle className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        )}
                        
                        {['sent', 'sending', 'paused'].includes(campaign.status) && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => navigate(`/admin/email/analytics/${campaign.id}`)} title="View Analytics">
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => navigate(`/admin/email/logs/${campaign.id}`)} title="View Logs">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        
                        {campaign.failed_count > 0 && (
                          <Button size="sm" variant="outline" onClick={() => exportFailedRecipients(campaign.id)} title="Export Failed Recipients" data-testid={`export-failed-btn-${campaign.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        
                        <Button size="sm" variant="outline" onClick={() => navigate(`/admin/email/preview/${campaign.id}`)} title="Preview">
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        <Button size="sm" variant="outline" onClick={() => duplicateCampaign(campaign.id)} title="Duplicate">
                          <Copy className="w-4 h-4" />
                        </Button>
                        
                        {!['sending', 'scheduled'].includes(campaign.status) && (
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteCampaign(campaign.id)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Row 2: Subject | Stats */}
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <p className="text-gray-600 truncate flex-1">
                        <span className="font-medium text-gray-700">Subject:</span> {campaign.subject}
                      </p>
                      <div className="flex items-center gap-4 text-gray-500 flex-shrink-0">
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {campaign.total_recipients || 0}
                        </span>
                        <span className="text-green-600 font-medium">{campaign.sent_count || 0} sent</span>
                        {(campaign.failed_count || 0) > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleFailuresPanel(campaign.id)}
                            className={`font-medium transition-colors ${
                              failureOpenFor === campaign.id
                                ? 'text-red-800 underline'
                                : 'text-red-600 hover:text-red-800 hover:underline'
                            }`}
                            title="Click to see why these failed"
                            data-testid={`failed-count-btn-${campaign.id}`}
                          >
                            {campaign.failed_count} failed
                          </button>
                        )}
                        <span className="text-gray-400">{formatDate(campaign.created_at)}</span>
                      </div>
                    </div>
                    
                    {/* Row 3: Progress Bar for Active Campaigns */}
                    {isActive && campaign.total_recipients > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <Progress value={progress} className="h-2" />
                          </div>
                          <span className="text-sm font-medium text-[#00457f] w-16 text-right">{progress}%</span>
                        </div>
                        
                        {/* Batch Information - Inline */}
                        {queueStatus && (
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                            {queueStatus.total_batches > 0 && (
                              <span className="flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded">
                                <Zap className="w-3 h-3 text-yellow-500" />
                                Batch {queueStatus.current_batch || 0}/{queueStatus.total_batches}
                              </span>
                            )}
                            {queueStatus.batch_size && (
                              <span className="bg-gray-50 px-2 py-0.5 rounded">{queueStatus.batch_size}/batch</span>
                            )}
                            {queueStatus.interval_minutes && (
                              <span className="bg-gray-50 px-2 py-0.5 rounded">every {queueStatus.interval_minutes}min</span>
                            )}
                            {queueStatus.next_batch_at && campaign.status === 'sending' && (
                              <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                                <Clock className="w-3 h-3" />
                                Next: {new Date(queueStatus.next_batch_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </span>
                            )}
                            {queueStatus.last_batch_failure_rate > 0 && (
                              <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded font-medium">
                                {(queueStatus.last_batch_failure_rate * 100).toFixed(1)}% failure rate
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Row 4: Failure diagnostics (expands when "X failed" is clicked) */}
                    {failureOpenFor === campaign.id && (
                      <div
                        className="mt-3 pt-3 border-t border-gray-100"
                        data-testid={`failure-panel-${campaign.id}`}
                      >
                        {failureDetails[campaign.id]?.loading && (
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Loading failure reasons…
                          </div>
                        )}
                        {failureDetails[campaign.id]?.error && (
                          <div className="text-sm text-red-600">
                            {failureDetails[campaign.id].error}
                          </div>
                        )}
                        {failureDetails[campaign.id]?.summary && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-semibold text-red-700">
                              <AlertCircle className="w-4 h-4" />
                              Why these failed ({failureDetails[campaign.id].total} total)
                            </div>
                            {failureDetails[campaign.id].summary.length === 0 ? (
                              <p className="text-sm text-gray-500 italic">
                                No per-recipient error details were recorded.
                              </p>
                            ) : (
                              <ul className="space-y-2">
                                {failureDetails[campaign.id].summary.map((bucket, idx) => (
                                  <li
                                    key={idx}
                                    className="border border-red-100 bg-red-50/60 rounded-lg p-3 text-sm"
                                    data-testid={`failure-bucket-${campaign.id}-${idx}`}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="font-medium text-red-800">
                                        {bucket.reason}
                                      </div>
                                      <span className="text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full flex-shrink-0">
                                        {bucket.count}×
                                      </span>
                                    </div>
                                    {bucket.hint && (
                                      <p className="mt-1 text-xs text-gray-700">
                                        <span className="font-semibold">Fix:</span> {bucket.hint}
                                      </p>
                                    )}
                                    {bucket.sample_recipients?.length > 0 && (
                                      <p className="mt-1 text-xs text-gray-500">
                                        Affected: {bucket.sample_recipients.join(', ')}
                                        {bucket.count > bucket.sample_recipients.length && ` + ${bucket.count - bucket.sample_recipients.length} more`}
                                      </p>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                            <div className="flex items-center justify-end gap-2 pt-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => exportFailedRecipients(campaign.id)}
                                data-testid={`failure-panel-export-${campaign.id}`}
                              >
                                <Download className="w-3 h-3 mr-1" />
                                Export CSV
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setFailureOpenFor(null)}
                                data-testid={`failure-panel-close-${campaign.id}`}
                              >
                                Close
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <QuickImportRecipientsModal
        open={!!quickImportCampaign}
        campaign={quickImportCampaign}
        onClose={() => setQuickImportCampaign(null)}
        onDone={fetchCampaigns}
      />
    </AdminLayout>
  );
};

export default EmailCampaigns;
